import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle2,
  ChevronRight,
  Crown,
  Monitor,
  Sparkles,
  Target,
  Trophy,
  Zap,
} from 'lucide-react';

const modules = [
  { n: '01', title: 'Fundamentos', level: 'Primeros pasos', text: 'Todo comienza por dominar las bases.' },
  { n: '02', title: 'Primeras estrategias', level: 'Primeros pasos', text: 'Las piezas ya se mueven. Ahora hay que aprender a coordinarlas.' },
  { n: '03', title: 'Principios de juego', level: 'Rating 0–600', text: 'Construir correctamente una partida desde las primeras jugadas.' },
  { n: '04', title: 'Desarrollo táctico', level: 'Rating 600–800', text: 'Comienza el verdadero entrenamiento táctico.' },
  { n: '05', title: 'Táctica y estrategia', level: 'Rating 800–1000', text: 'Más recursos para atacar, defender y finalizar una partida.' },
  { n: '06', title: 'Juego dinámico', level: 'Rating 1000–1200', text: 'El juego aumenta en profundidad y exigencia.' },
  { n: '07', title: 'Pensamiento estratégico', level: 'Rating 1200–1450', text: 'De los recursos tácticos a conceptos estratégicos más profundos.' },
  { n: '08', title: 'Evaluación y decisiones', level: 'Rating 1450–1700', text: 'Comprender la posición antes de elegir el camino.' },
  { n: '09', title: 'Juego avanzado', level: 'Rating 1700–1900', text: 'Profundizar en la valoración y el juego posicional.' },
  { n: '10', title: 'Dominio avanzado', level: 'Rating 1900–2200', text: 'Evaluar, comprender y encontrar soluciones en posiciones complejas.' },
];

const benefits = [
  {
    icon: Brain,
    title: 'Pensamiento estratégico',
    text: 'Planear, evaluar alternativas y anticipar las siguientes jugadas.',
  },
  {
    icon: Target,
    title: 'Concentración',
    text: 'Prestar atención a los detalles y sostener el foco en una tarea.',
  },
  {
    icon: Zap,
    title: 'Resolución de problemas',
    text: 'Explorar caminos y aprender de cada decisión.',
  },
  {
    icon: Trophy,
    title: 'Disciplina y constancia',
    text: 'Practicar con propósito y avanzar paso a paso.',
  },
];

const platformFeatures = [
  {
    icon: BookOpen,
    title: 'Aprende',
    text: 'Lecciones, explicaciones y estudios diseñados para avanzar por niveles.',
  },
  {
    icon: Target,
    title: 'Practica',
    text: 'Ejercicios interactivos, retos y actividades directamente sobre el tablero.',
  },
  {
    icon: Monitor,
    title: 'Entrena',
    text: 'Una experiencia digital pensada para complementar las clases en vivo.',
  },
  {
    icon: Trophy,
    title: 'Progresa',
    text: 'Puntos, logros, niveles y seguimiento para convertir el avance en motivación.',
  },
];

function ChessPreview() {
  const pieces = {
    a8: '♜', b8: '♞', c8: '♝', d8: '♛', e8: '♚', f8: '♝', g8: '♞', h8: '♜',
    a7: '♟', b7: '♟', c7: '♟', d7: '♟', e7: '♟', f7: '♟', g7: '♟', h7: '♟',
    e2: '♙', a2: '♙', b2: '♙', c2: '♙', d2: '♙', f2: '♙', g2: '♙', h2: '♙',
    a1: '♖', b1: '♘', c1: '♗', d1: '♕', e1: '♔', f1: '♗', g1: '♘', h1: '♖',
  };

  return (
    <div className="relative mx-auto w-full max-w-[540px]">
      <div className="absolute -inset-8 rounded-[3rem] bg-blue-100/70 blur-3xl" />

      <div className="relative rounded-[2rem] border border-slate-200 bg-white p-4 shadow-[0_30px_80px_-35px_rgba(15,23,42,0.45)]">
        <div className="mb-3 px-2">
          <div className="font-display text-sm font-extrabold text-slate-900">Entrenamiento Chess4Kings</div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <div className="grid aspect-square grid-cols-8 grid-rows-8">
            {Array.from({ length: 64 }).map((_, i) => {
              const file = String.fromCharCode(97 + (i % 8));
              const rank = 8 - Math.floor(i / 8);
              const square = `${file}${rank}`;
              const dark = (i + Math.floor(i / 8)) % 2 === 1;
              return (
                <div
                  key={square}
                  className={[
                    'relative flex items-center justify-center text-[clamp(1.5rem,4vw,3rem)] leading-none',
                    dark ? 'bg-[#2B5D8A]' : 'bg-[#E9F1F8]',
                  ].join(' ')}
                >
                  {pieces[square] && (
                    <span className={pieces[square].charCodeAt(0) >= 9818 && pieces[square].charCodeAt(0) <= 9823 ? 'text-slate-900 drop-shadow-[0_2px_1px_rgba(255,255,255,0.45)]' : 'text-white drop-shadow-[0_3px_2px_rgba(15,23,42,0.55)]'}>
                      {pieces[square]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>

    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen overflow-hidden bg-[#F8FAFC] text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img
              src="/branding/chess4kings-king-blue.png"
              alt="Chess4Kings"
              className="h-10 w-10 object-contain"
            />
            <div className="leading-none">
              <div className="font-display text-lg font-extrabold tracking-tight text-slate-950">
                Chess<span className="text-blue-600">4</span>Kings
              </div>
              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400">
                Academia de ajedrez
              </div>
            </div>
          </Link>

          <div className="hidden items-center gap-7 text-sm font-semibold text-slate-600 lg:flex">
            <a href="#metodologia" className="transition hover:text-blue-600">Metodología</a>
            <a href="#plataforma" className="transition hover:text-blue-600">Plataforma</a>
            <a href="#desarrollo" className="transition hover:text-blue-600">Desarrollo</a>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="font-semibold" data-testid="nav-login-btn">
                Iniciar sesión
              </Button>
            </Link>
            <a href="https://wa.me/525527780611?text=Hola%2C%20quiero%20agendar%20mi%20primera%20clase%20de%20Chess4Kings." target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-blue-600 px-5 font-bold shadow-lg shadow-blue-600/20 hover:bg-blue-700" data-testid="nav-whatsapp-btn">
                Agenda tu primera clase
              </Button>
            </a>
          </div>
        </nav>
      </header>

      <main>
        <section className="relative">
          <div className="absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(circle_at_72%_18%,rgba(37,99,235,0.13),transparent_34%),radial-gradient(circle_at_15%_20%,rgba(30,64,175,0.07),transparent_28%)]" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-16 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:pb-32 lg:pt-24">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                <Sparkles className="h-3.5 w-3.5" />
                Escuela de ajedrez · Desarrollo cognitivo
              </div>

              <h1 className="max-w-2xl font-display text-5xl font-extrabold leading-[0.98] tracking-[-0.04em] text-slate-950 sm:text-6xl lg:text-7xl">
                Ajedrez para
                <span className="block text-blue-600">aprender a pensar.</span>
              </h1>

              <p className="mt-7 max-w-xl font-display text-2xl font-semibold leading-tight text-slate-700 sm:text-3xl">
                Aprende. Practica. Mejora.
              </p>

              <p className="mt-5 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
                Una experiencia Chess4Kings para aprender ajedrez, practicar en el tablero y avanzar de acuerdo con tus conocimientos, habilidades y constancia.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="https://wa.me/525527780611?text=Hola%2C%20quiero%20agendar%20mi%20primera%20clase%20de%20Chess4Kings." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="h-12 rounded-full bg-blue-600 px-7 font-bold shadow-xl shadow-blue-600/20 hover:bg-blue-700" data-testid="hero-cta-whatsapp">
                    Agenda tu primera clase
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#metodologia">
                  <Button size="lg" variant="outline" className="h-12 rounded-full border-slate-300 px-7 font-bold">
                    Conoce el método
                  </Button>
                </a>
              </div>

              <div className="mt-10 grid max-w-lg grid-cols-3 border-t border-slate-200 pt-6">
                <div>
                  <div className="font-display text-2xl font-extrabold text-slate-950">10</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Niveles</div>
                </div>
                <div className="border-l border-slate-200 pl-5">
                  <div className="font-display text-2xl font-extrabold text-slate-950">∞</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Práctica</div>
                </div>
                <div className="border-l border-slate-200 pl-5">
                  <div className="font-display text-2xl font-extrabold text-slate-950">C4K</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Método</div>
                </div>
              </div>
            </div>

            <ChessPreview />
          </div>
        </section>

        <section id="metodologia" className="border-y border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <div className="grid gap-10 lg:grid-cols-[0.65fr_1.35fr]">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Metodología Chess4Kings</div>
                <h2 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-5xl">
                  Diez pasos
                  <span className="block text-slate-400">para crecer.</span>
                </h2>
                <p className="mt-5 max-w-md leading-7 text-slate-600">
                  Desde el primer movimiento hasta la comprensión avanzada del juego. Una ruta progresiva de estrategia, táctica, aperturas y finales.
                </p>
                <div className="mt-7 flex items-center gap-2 text-sm font-bold text-slate-900">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Avanza según tu aprendizaje
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {modules.map((module) => (
                  <article
                    key={module.n}
                    className="group rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="font-mono text-xs font-semibold text-blue-600">{module.n}</span>
                      <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-1 group-hover:text-blue-500" />
                    </div>
                    <h3 className="mt-7 font-display text-xl font-extrabold text-slate-950">{module.title}</h3>
                    <div className="mt-1 text-xs font-bold uppercase tracking-wider text-blue-600">{module.level}</div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{module.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="plataforma" className="bg-slate-950 text-white">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <div className="grid items-center gap-14 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">Tu entrenamiento</div>
                <h2 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
                  Todo tu progreso,
                  <span className="block text-blue-400">en un solo lugar.</span>
                </h2>
                <p className="mt-5 max-w-xl leading-7 text-slate-300">
                  La plataforma acompaña las clases y convierte cada sesión en una oportunidad para aprender, practicar y demostrar lo que sabes.
                </p>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {platformFeatures.map(({ icon: Icon, title, text }) => (
                    <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                      <Icon className="h-5 w-5 text-blue-400" />
                      <h3 className="mt-4 font-display font-extrabold">{title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-400">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-10 bg-blue-600/20 blur-3xl" />
                <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white p-3 shadow-2xl">
                  <img
                    src="/branding/chess4kings-dashboard-banner-master-crop.png"
                    alt="Plataforma Chess4Kings"
                    className="w-full rounded-[1.5rem] object-cover"
                  />
                  <div className="absolute bottom-7 left-7 right-7 rounded-2xl border border-white/60 bg-white/95 p-4 text-slate-900 shadow-xl backdrop-blur">
                    <div className="flex items-center gap-3">
                      <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600 text-white">
                        <Crown className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-display font-extrabold">Tu siguiente nivel empieza aquí.</div>
                        <div className="text-xs text-slate-500">Aprende · Practica · Mejora</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="desarrollo" className="bg-[#F8FAFC]">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Más que ajedrez</div>
              <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-950 sm:text-5xl">
                Lo que el ajedrez
                <span className="block text-slate-400">entrena en ellos.</span>
              </h2>
              <p className="mt-5 leading-7 text-slate-600">
                Cada partida es una oportunidad para observar, decidir y aprender. Habilidades que se siguen practicando dentro y fuera del tablero.
              </p>
            </div>

            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {benefits.map(({ icon: Icon, title, text }, index) => (
                <article key={title} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-blue-50 text-blue-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-mono text-xs text-slate-300">0{index + 1}</span>
                  </div>
                  <h3 className="mt-7 font-display text-xl font-extrabold text-slate-950">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="rounded-[2rem] bg-blue-600 px-7 py-12 text-white shadow-2xl shadow-blue-600/20 sm:px-12 lg:flex lg:items-center lg:justify-between lg:px-16">
              <div className="max-w-2xl">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-100">Chess4Kings</div>
                <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
                  Tu próxima gran jugada empieza aquí.
                </h2>
                <p className="mt-4 max-w-xl leading-7 text-blue-100">
                  Aprende, practica y mejora con una ruta diseñada para crecer paso a paso.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3 lg:mt-0 lg:justify-end">
                <a href="https://wa.me/525527780611?text=Hola%2C%20quiero%20agendar%20mi%20primera%20clase%20de%20Chess4Kings." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="h-12 rounded-full bg-white px-7 font-bold text-blue-700 hover:bg-blue-50" data-testid="final-cta-whatsapp">
                    Agenda tu primera clase
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="h-12 rounded-full border-white/40 bg-transparent px-7 font-bold text-white hover:bg-white/10" data-testid="final-cta-login">
                    Ya soy alumno
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src="/branding/chess4kings-king-blue.png" alt="Chess4Kings" className="h-8 w-8 object-contain" />
            <div>
              <div className="font-display font-extrabold text-slate-950">Chess4Kings</div>
              <div className="text-xs text-slate-400">Ajedrez para aprender a pensar.</div>
            </div>
          </Link>
          <div className="text-xs text-slate-400">© 2026 Chess4Kings. Todos los derechos reservados.</div>
        </div>
      </footer>
    </div>
  );
}
